import React, { type ComponentType, useMemo, useState } from 'react';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { Redirect } from '../../router/react-router-v5-compat';
import i18n from '../../i18n';
import './scss/print-module.page.scss';
import { HookDecorator } from '../main/main-page-states';
import { AuxiliaryModeToolbarToggle } from '../../component/auxiliary-mode-toolbar-toggle/auxiliary-mode-toolbar-toggle.component';
import { PrintModuleAuxiliaryModeContext } from './print-module-auxiliary-mode-context';
import PrintSettingPage from './print-setting.page';
import NativeProtocolPrint from './native-protocol-print.page';
import PrintDevicePage from './print-device.page';
import PrintGroupPage from './print-group.page';
import PrintTemplate from './print-template/print-template.component';
import PrintQueue from './print-template/print-queue.componrnt';
import PrintHeadPage from './print-head.page';
import PrintTailPage from './print-tail.page';

/** 云 RBP「打印」模块路由前缀 */
const PRINT_MODULE_BASE = '/main/rbp-cloud/print';

const TAB_DEFS: {
    path: string;
    i18nKey: string;
    Component: ComponentType;
}[] = [
    { path: `${PRINT_MODULE_BASE}/basic`, i18nKey: 'print_basic_setting_page_label', Component: PrintSettingPage },
    { path: `${PRINT_MODULE_BASE}/native-protocol`, i18nKey: 'print_native_protocol_page_label', Component: NativeProtocolPrint },
    { path: `${PRINT_MODULE_BASE}/device`, i18nKey: 'dis_setting_page_print_machine_setup', Component: PrintDevicePage },
    { path: `${PRINT_MODULE_BASE}/kwa-group`, i18nKey: 'print_group_and_KWA_job_page_label', Component: PrintGroupPage },
    { path: `${PRINT_MODULE_BASE}/template`, i18nKey: 'print_template_page_label', Component: PrintTemplate },
    { path: `${PRINT_MODULE_BASE}/head`, i18nKey: 'print_head_page_setting', Component: PrintHeadPage },
    { path: `${PRINT_MODULE_BASE}/tail`, i18nKey: 'print_tail_page_setting', Component: PrintTailPage },
    { path: `${PRINT_MODULE_BASE}/queue`, i18nKey: 'print_queue_page_label', Component: PrintQueue },
];

const DEFAULT_TAB_PATH = TAB_DEFS[0].path;

/**
 * 云 RBP「打印」壳页：顶部 Tab 切换各子页（逻辑与接口平迁自 RBP2，请求走 `apiManage_rbp`）。
 */
export default HookDecorator(() => {
    const history = useHistory();
    const { pathname } = useLocation();
    const [auxiliaryModeEnabled, setAuxiliaryModeEnabled] = useState(false);
    const auxiliaryModeContextValue = useMemo(
        () => ({ auxiliaryModeEnabled, setAuxiliaryModeEnabled }),
        [auxiliaryModeEnabled],
    );

    if (pathname === PRINT_MODULE_BASE || pathname === `${PRINT_MODULE_BASE}/`) {
        return <Redirect to={DEFAULT_TAB_PATH} />;
    }
    const active = TAB_DEFS.find((t) => t.path === pathname);
    if (!active) {
        return <Redirect to={DEFAULT_TAB_PATH} />;
    }

    const { Component } = active;

    return (
        <PrintModuleAuxiliaryModeContext.Provider value={auxiliaryModeContextValue}>
            <Box className="print-module-page">
                <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={2}
                    className="print-module-page__toolbar"
                >
                    <Tabs
                        className="print-module-page__tabs"
                        sx={{ flex: 1, minWidth: 0 }}
                        value={pathname}
                        onChange={(_, value: string) => history.push(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        TabIndicatorProps={{ style: { display: 'none' } }}
                    >
                        {TAB_DEFS.map((t) => (
                            <Tab
                                key={t.path}
                                value={t.path}
                                disableRipple
                                label={i18n.t(t.i18nKey)}
                                className="print-module-page__tab"
                            />
                        ))}
                    </Tabs>
                    <AuxiliaryModeToolbarToggle
                        enabled={auxiliaryModeEnabled}
                        onToggle={() => setAuxiliaryModeEnabled(!auxiliaryModeEnabled)}
                    />
                </Stack>
                <Box className="print-module-page__panel">
                    <Component />
                </Box>
            </Box>
        </PrintModuleAuxiliaryModeContext.Provider>
    );
});
